import React from 'react';
import _ from 'lodash';
import { Autocomplete, CircularProgress, TextField, Button, Table, TableContainer, TableCell, TableHead, TableBody, TableRow, Stack, Select, MenuItem, Menu, IconButton } from '@mui/material';
import { DebounceInput } from 'react-debounce-input';
import { useQuery, useMutation } from 'react-fetching-library';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getTemplateProductsAction, submitProductAction } from '../../../../../../../services/request';
import { vatRates } from '../../../../../../../services/constants';
import { AlertDialogContainer } from '../../../../../../../components-core/others/AlertDialogContainer';
import { CssTextField } from '../../../../../../../components-core/others/CssTextField';
import { dec2fixed, recalculate } from './recalculate';
import { validateValues, validateRequiredFields } from '../../../../../../../services/functions';

const Row = React.forwardRef((props, ref) => {
  const { row, index, removeProductCallback, saveProductCallback, updateProductCallback } = props;
  const [name, setName] = React.useState(row.name || '');
  const [quantity, setQuantity] = React.useState(row.quantity || '');
  const [unit, setUnit] = React.useState(row.unit || '');
  const [pkwiu, setPkwiu] = React.useState(row.pkwiu || '');
  const [grossPrice, setGrossPrice] = React.useState(row.gross_price || '');
  const [netPrice, setNetPrice] = React.useState(row.net_price || '');
  const [netValue, setNetValue] = React.useState(row.net_value || '');
  const [vatRate, setVatRate] = React.useState(row.vat_rate || '');
  const [vatAmount, setVatAmount] = React.useState(row.vat_amount || '');
  const [grossValue, setGrossValue] = React.useState(row.gross_value || '');
  const [errors, setErrors] = React.useState([]);
  const [requiredFields, setRequiredFields] = React.useState([`name${index}`]);
  const [additionalOptionsAnchor, setAdditionalOptionsAnchor] = React.useState(null);
  const openAdditionalOptionsAnchor = Boolean(additionalOptionsAnchor);
  const validateObject = { id: index, errors, requiredFields };
  React.useImperativeHandle(ref, () => ({ getErrors: () => validateObject }), [errors]);

  React.useEffect(() => {
    setName(row.name || '');
    setQuantity(row.quantity || '');
    setUnit(row.unit || '');
    setPkwiu(row.pkwiu || '');
    setGrossPrice(row.gross_price || '');
    setNetPrice(row.net_price || '');
    setNetValue(row.net_value || '');
    setVatRate(row.vat_rate || '');
    setVatAmount(row.vat_amount || '');
    setGrossValue(row.gross_value || '');
  }, [row]);

  React.useEffect(() => {
    updateProductCallback(constructProduct());
  }, [name, quantity, unit, pkwiu, grossPrice, netPrice, netValue, vatRate, vatAmount, grossValue]);

  React.useEffect(() => {
    if (name) {
      setRequiredFields([]);
    }
  }, []);

  const transform = val => val && (!isNaN(val) || !isNaN(parseFloat(val))) ? val.toString().replace(',', '.') : val;

  const recalculateSignal = (name, value) => {
    const { newQuantity, newGrossPrice, newNetPrice, newNetValue, newVatRate, newVatAmount, newGrossValue } =
      recalculate(name, transform(value), transform(quantity),
        transform(grossPrice), transform(netPrice), transform(netValue),
        vatRate, transform(vatAmount), transform(grossValue));
    if (newQuantity !== quantity) {
      setQuantity(newQuantity);
    }
    if (newGrossPrice !== grossPrice) {
      setGrossPrice(newGrossPrice);
    }
    if (newNetPrice !== netPrice) {
      setNetPrice(newNetPrice);
    }
    if (newNetValue !== netValue) {
      setNetValue(newNetValue);
    }
    if (newVatRate != vatRate) {
      setVatRate(newVatRate);
    }
    if (newVatAmount !== vatAmount) {
      setVatAmount(newVatAmount);
    }
    if (newGrossValue !== grossValue) {
      setGrossValue(newGrossValue);
    }
  };

  const constructProduct = () => (
    { id: row.id, name, quantity, unit, pkwiu, gross_price: grossPrice, net_price: netPrice,
      net_value: netValue, vat_rate: vatRate, vat_amount: vatAmount, gross_value: grossValue });

  const validateValue = (value, type, maxLength, name, required) => {
    if (type === 'float') {
      setErrors(validateValues(errors, transform(value), type, maxLength, name));
    } else {
      setErrors(validateValues(errors, value, type, maxLength, name));
    }
    if (required) {
      setRequiredFields(validateRequiredFields(requiredFields, value, name, required));
    }
  };

  return (
    <React.Fragment>
      <TableRow key={row}>
        <TableCell>
          <DebounceInput
            name={`name${index}`}
            debounceTimeout={1000}
            value={name}
            element={CssTextField}
            onChange={e => {
              validateValue(
                e.target.value,
                'string',
                255,
                e.target.name,
                true
              );
              setName(e.target.value);
            }}
            variant="outlined"
            multiline
            error={errors.includes(`name${index}`) || requiredFields.includes(`name${index}`) ? true : false}
            helperText={
              errors.includes(`name${index}`)
                ? 'Przekroczono maksymalną długość'
                : requiredFields.includes(`name${index}`)
                  ? 'To pole jest wymagane'
                  : ''
            }

          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name={`quantity${index}`}
            debounceTimeout={1000}
            value={quantity}
            element={CssTextField}
            onChange={e => {
              validateValue(
                e.target.value,
                'number',
                null,
                e.target.name,
                true
              );
              setQuantity(e.target.value);
              recalculateSignal('quantity', e.target.value);
            }}
            variant="outlined"
            error={errors.includes(`quantity${index}`) || requiredFields.includes(`quantity${index}`) ? true : false}
            helperText={
              errors.includes(`quantity${index}`)
                ? 'Proszę wprowadzić odpowiednią liczbę całkowitą'
                : requiredFields.includes(`quantity${index}`)
                  ? 'To pole jest wymagane'
                  : ''
            }
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name="unit"
            debounceTimeout={1000}
            value={unit}
            element={CssTextField}
            onChange={e => setUnit(e.target.value)}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name="pkwiu"
            debounceTimeout={1000}
            value={pkwiu}
            element={CssTextField}
            onChange={e => setPkwiu(e.target.value)}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name={`gross_price${index}`}
            debounceTimeout={1000}
            value={grossPrice}
            element={CssTextField}
            onChange={e => {
              validateValue(
                e.target.value,
                'float',
                null,
                e.target.name,
                true
              );
              setGrossPrice(e.target.value);
              recalculateSignal('gross_price', e.target.value);
            }}
            variant="outlined"
            error={errors.includes(`gross_price${index}`) || requiredFields.includes(`gross_price${index}`) ? true : false}
            helperText={
              errors.includes(`gross_price${index}`)
                ? 'Proszę wprowadzić odpowiednią liczbę'
                : requiredFields.includes(`gross_price${index}`)
                  ? 'To pole jest wymagane'
                  : ''
            }
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name={`net_price${index}`}
            debounceTimeout={1000}
            value={netPrice}
            element={CssTextField}
            onChange={e => {
              validateValue(
                e.target.value,
                'float',
                null,
                e.target.name,
                true
              );
              setNetPrice(e.target.value);
              recalculateSignal('net_price', e.target.value);
            }}
            variant="outlined"
            error={errors.includes(`net_price${index}`) || requiredFields.includes(`net_price${index}`) ? true : false}
            helperText={
              errors.includes(`net_price${index}`)
                ? 'Proszę wprowadzić odpowiednią liczbę'
                : requiredFields.includes(`net_price${index}`)
                  ? 'To pole jest wymagane'
                  : ''
            }
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name="net_value"
            debounceTimeout={1000}
            value={netValue}
            element={CssTextField}
            onChange={e => {
              setNetValue(e.target.value);
              // recalculateSignal('net_value', e.target.value);
            }}
            variant="outlined"
            disabled
          />
        </TableCell>
        <TableCell>
          <Select
            labelId="state-label"
            id="state"
            value={vatRate}
            label="state"
            onChange={e => {
              setVatRate(e.target.value);
              recalculateSignal('vat_rate', e.target.value);
            }}
          >
            {_.map(vatRates, item => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))
            }
          </Select>
        </TableCell>
        <TableCell>
          <DebounceInput
            name="vat_amount"
            debounceTimeout={1000}
            value={vatAmount}
            element={CssTextField}
            onChange={e => {
              setVatAmount(e.target.value);
              // recalculateSignal('vat_amount', e.target.value);
            }}
            variant="outlined"
            disabled
          />
        </TableCell>
        <TableCell>
          <DebounceInput
            name="gross_value"
            debounceTimeout={1000}
            value={grossValue}
            element={CssTextField}
            onChange={e => {
              setGrossValue(e.target.value);
              // recalculateSignal('gross_value', e.target.value);
            }}
            variant="outlined"
            disabled
          />
        </TableCell>
        <TableCell>
          <Stack direction="row">
            <IconButton
              color="error"
              variant="contained"
              onClick={() => removeProductCallback(row.id)}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={openAdditionalOptionsAnchor ? 'long-menu' : undefined}
              aria-expanded={openAdditionalOptionsAnchor ? 'true' : undefined}
              aria-haspopup="true"
              onClick={e => setAdditionalOptionsAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button'
              }}
              anchorEl={additionalOptionsAnchor}
              open={openAdditionalOptionsAnchor}
              onClose={() => setAdditionalOptionsAnchor(null)}
              PaperProps={{
                style: {
                  width: '30ch'
                }
              }}
            >
              <MenuItem onClick={() => saveProductCallback(constructProduct())}>
              Zapisz do kartoteki produktów
              </MenuItem>
            </Menu>
          </Stack>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
});

export const Position = React.forwardRef((props, ref) => {
  const { receiptsProducts, order, editData, initProduct } = props;
  const [products, setProducts] = React.useState([]);
  React.useImperativeHandle(ref, () => ({ getProducts: () => products, getAllErrors: () => getAllErrors() }), [products]);
  const dynamicRef = React.useRef([]);
  const { mutate } = useMutation(submitProductAction);
  const { loading, payload, error } = useQuery(getTemplateProductsAction());
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [openTemplateProducts, setOpenTemplateProducts] = React.useState(false);
  const [templateProducts, setTemplateProducts] = React.useState([]);
  const loadingTemplateProducts = openTemplateProducts && templateProducts.length === 0;

  React.useEffect(() => {
    if (!_.isEmpty(editData)) {
      setProducts(editData.products);
    } else if (receiptsProducts && receiptsProducts.length === 0) {
      setProducts([]);
    } else if (receiptsProducts && receiptsProducts.length > 0) {
      setProducts([...receiptsProducts]);
    }
  }, [receiptsProducts, editData]);

  React.useEffect(() => {
    if (initProduct) {
      setProducts([initProduct]);
    }
  }, [initProduct]);

  React.useEffect(() => {
    if (!error && !loading) {
      setTemplateProducts(payload.results);
    }
  }, [loadingTemplateProducts, loading]);

  React.useEffect(() => {
    if (!openTemplateProducts) {
      setTemplateProducts([]);
    }
  }, [openTemplateProducts]);

  const addEmptyProduct = () => {
    setProducts([...products, { id: Date.now(), name: '', quantity: '1', unit: '', pkwiu: '', gross_price: '0', net_price: '0', net_value: '0', vat_rate: '23', vat_amount: '0', gross_value: '0' }]);
  };

  const removeProductCallback = id => {
    setProducts(_.filter(products, obj => obj.id !== id));
  };

  const saveProductCallback = async product => {
    product.template = true;
    product.order = order.id;
    const { error, payload } = await mutate(product);
    if (!error) {
      setSeverity('success');
      setTitle('Dodano nowy szablon produktu');
      setShowAlert(true);
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać szablonu produktu');
      setMessage(JSON.stringify(payload));
      setShowAlert(true);
    }
  };

  const updateProductCallback = product => {
    const cloneProducts = _.cloneDeep(products);
    const index = cloneProducts.findIndex(obj => obj.id == product.id);
    cloneProducts.splice(index, 1);
    cloneProducts.splice(index, 0, product);
    setProducts(cloneProducts);
  };

  const usedVatRates = _.uniq(_.map(products, 'vat_rate'));

  const getAllErrors = () => {
    const errorsArr = [];
    _.map(products, (product, key) => {
      errorsArr.push(dynamicRef.current[key].getErrors());
    });

    return errorsArr;
  };

  return (
    <TableContainer sx={{ minWidth: 1200 }}>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Stack direction="row" sx={{ mb: 1, mt: 1 }}>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 1, mt: 1, mr: 1 }} onClick={() => addEmptyProduct()}>
        Dodaj pozycję
        </Button>
        <Autocomplete
          sx={{ width: 300 }}
          open={openTemplateProducts}
          onOpen={() => {
            setOpenTemplateProducts(true);
          }}
          onClose={() => {
            setOpenTemplateProducts(false);
          }}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          getOptionLabel={option => option.name}
          options={templateProducts}
          loading={loadingTemplateProducts}
          onChange={(e, item) => !_.isNil(item) ? setProducts([...products, item]) : null}
          renderInput={params => (
            <TextField
              {...params}
              label="Wybierz szablon pozycji..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loadingTemplateProducts ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />

      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nazwa produktu</TableCell>
            <TableCell>Ilość</TableCell>
            <TableCell>J.m.</TableCell>
            <TableCell>CN / PKWiU</TableCell>
            <TableCell>Cena brutto [PLN]</TableCell>
            <TableCell>Cena netto [PLN]</TableCell>
            <TableCell>Wartość netto [PLN]</TableCell>
            <TableCell>VAT [%]</TableCell>
            <TableCell>Wartość VAT [PLN]</TableCell>
            <TableCell>Wartość brutto [PLN]</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product, key) => (
            <Row
              ref={el => (dynamicRef.current[key] = el)}
              row={product}
              removeProductCallback={removeProductCallback}
              saveProductCallback={saveProductCallback}
              key={key}
              index={key}
              updateProductCallback={updateProductCallback} />
          ))}
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell>Razem:</TableCell>
            <TableCell>{dec2fixed(_.sumBy(products, obj => parseFloat(obj.net_value)))}</TableCell>
            <TableCell>X</TableCell>
            <TableCell>{dec2fixed(_.sumBy(products, obj => parseFloat(obj.vat_amount)))}</TableCell>
            <TableCell>{dec2fixed(_.sumBy(products, obj => parseFloat(obj.gross_value)))}</TableCell>
          </TableRow>
          {usedVatRates.length > 0 && _.map(usedVatRates, rate => (
            <TableRow key={rate}>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell>W tym:</TableCell>
              <TableCell>{dec2fixed(_.sumBy(_.filter(products, product => product.vat_rate === rate), obj => parseFloat(obj.net_value)))}</TableCell>
              <TableCell>{rate} %</TableCell>
              <TableCell>{dec2fixed(_.sumBy(_.filter(products, product => product.vat_rate === rate), obj => parseFloat(obj.vat_amount)))}</TableCell>
              <TableCell>{dec2fixed(_.sumBy(_.filter(products, product => product.vat_rate === rate), obj => parseFloat(obj.gross_value)))}</TableCell>
            </TableRow>
          ))

          }
        </TableBody>
      </Table>
    </TableContainer>
  );
});
